import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, InputBase } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LEVELS } from "./constants";

const FieldLabel = ({ children, required, t }) => (
  <Typography
    sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "8px",
      letterSpacing: "0.15em",
      textTransform: "uppercase",
      color: t.text3,
      mb: "5px",
    }}
  >
    {children}
    {required && <span style={{ color: "#28B3B4", marginLeft: "3px" }}>*</span>}
  </Typography>
);


const Field = ({
  value,
  onChange,
  placeholder,
  error,
  inputProps,
  disabled,
  t,
  isDark,
}) => (
  
  <Box
    sx={{
      background: t.surface2,
      border: `1px solid ${error ? "#c84040" : t.border2}`,
      borderRadius: "3px",
      padding: "7px 10px",
      transition: "border-color 0.15s",
      opacity: 1,
      pointerEvents: disabled ? "none" : "auto",
      "&:focus-within": { borderColor: error ? "#c84040" : t.accent },
    }}
  >
    <InputBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      inputProps={{
        ...inputProps,
        style: {
          colorScheme: isDark ? "dark" : "light",
          ...(inputProps?.style ?? {}),
        },
      }}
      disabled={disabled}
      sx={{
        width: "100%",
        fontFamily: "'Inter', sans-serif",
        fontSize: "13px",
        color: t.text,
        "& input::placeholder": { color: t.text3, fontSize: "12px" },
      }}
    />
  </Box>
);

const FieldError = ({ message }) => (
  <Typography
    sx={{
      fontSize: "10px",
      color: "#c84040",
      mt: "4px",
      fontFamily: "'DM Mono', monospace",
    }}
  >
    {message}
  </Typography>
);

const LevelDropdown = ({ value, onChange, error, disabled, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = LEVELS.find((l) => l.code === value);

  return (
    <Box ref={ref} sx={{ position: "relative" }}>
      <Box
        onClick={() => !disabled && setOpen((o) => !o)}
        sx={{
          background: t.surface2,
          border: `1px solid ${error ? "#c84040" : t.border2}`,
          borderRadius: "3px",
          padding: "7px 10px",
          cursor: disabled ? "not-allowed" : "pointer",
          pointerEvents: disabled ? "none" : "auto",
          userSelect: "none",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          transition: "border-color 0.15s",
          "&:hover": { borderColor: disabled ? t.border2 : t.accent + "66" },
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "13px",
            color: selected ? t.text : t.text3,
          }}
        >
          {selected ? `${selected.code} — ${selected.title}` : "Select level"}
        </Typography>
        <Typography sx={{ color: t.text3, fontSize: "10px" }}>▾</Typography>
      </Box>

      {open && !disabled && (
        <Box
          sx={{
            position: "absolute",
            top: "calc(100% + 2px)",
            left: 0,
            right: 0,
            zIndex: 300,
            background: t.surface,
            border: `1px solid ${t.border2}`,
            borderRadius: "3px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
            overflow: "hidden",
          }}
        >
          {LEVELS.map((lvl, i) => (
            <Box
              key={lvl.code}
              onClick={() => {
                onChange(lvl.code);
                setOpen(false);
              }}
              sx={{
                padding: "7px 10px",
                fontSize: "12px",
                fontFamily: "'Inter', sans-serif",
                color: t.text2,
                cursor: "pointer",
                borderBottom:
                  i < LEVELS.length - 1 ? `1px solid ${t.border}` : "none",
                transition: "all 0.12s",
                "&:hover": { background: t.surface2, color: t.text },
              }}
            >
              {lvl.code} — {lvl.title}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const IDPreview = ({ isEdit, positionCode, positionType, t }) => (
  <Box
    sx={{
      background: t.surface2,
      border: `1px solid ${t.border2}`,
      borderRadius: "3px",
      padding: "12px 14px",
      mb: "4px",
    }}
  >
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "8px",
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: t.text3,
        mb: "6px",
      }}
    >
      Position ID
    </Typography>
    <Typography
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontSize: "28px",
        fontWeight: 700,
        color: t.accent,
        letterSpacing: "0.02em",
        lineHeight: 1,
      }}
    >
      {isEdit ? positionCode : `${positionType}-NNN`}
    </Typography>
    {!isEdit && (
      <Typography
        sx={{
          fontSize: "10.5px",
          color: t.text3,
          fontWeight: 300,
          mt: "4px",
        }}
      >
        Sequence number auto-assigned on save
      </Typography>
    )}
  </Box>
);

const PositionForm = ({
  roleTitle,
  onRoleChange,
  levelCode,
  onLevelChange,
  startDate,
  onStartChange,
  endDate,
  onEndChange,
  positionType,
  errors,
  isEdit,
  positionCode,
  isAllocated,
  readOnly,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const t = theme.custom;

  return (
    <>
      <Box sx={{ mb: "10px" }}>
        <FieldLabel required t={t}>
          Role / Job Title
        </FieldLabel>
        <Field
          value={roleTitle}
          onChange={onRoleChange}
          placeholder="e.g. Senior Data Engineer"
          error={!!errors.roleTitle}
          disabled={readOnly}
          t={t}
        />
        {errors.roleTitle && <FieldError message={errors.roleTitle} />}
      </Box>

      <Box sx={{ mb: "10px" }}>
        <FieldLabel required t={t}>
          Level
        </FieldLabel>
        <LevelDropdown
          value={levelCode}
          onChange={onLevelChange}
          error={!!errors.levelCode}
          disabled={readOnly}
          t={t}
        />
        {errors.levelCode && <FieldError message={errors.levelCode} />}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px",
          mb: "10px",
        }}
      >
        <Box>
          <FieldLabel t={t}>Start Date</FieldLabel>
<Field
  value={startDate}
  onChange={onStartChange}
  disabled={isAllocated || readOnly}
  placeholder="YYYY-MM-DD"
  inputProps={{ type: "date" }}
  t={t}
  isDark={isDark}
/>
        </Box>
        <Box>
          <FieldLabel t={t}>End Date</FieldLabel>
          <Field
  value={endDate}
  onChange={onEndChange}
  placeholder="YYYY-MM-DD"
  disabled={readOnly}
  inputProps={{ type: "date" }}
  t={t}
  isDark={isDark}   // ← add this
/>
        </Box>
      </Box>

      <IDPreview
        isEdit={isEdit}
        positionCode={positionCode}
        positionType={positionType}
        t={t}
      />
    </>
  );
};

export default PositionForm;
